import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
	Box,
	Container,
	Tabs,
	Tab,
	Card,
	CardContent,
	Button,
	Typography,
	Breadcrumbs,
	Link,
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import { useLazyQuery, useMutation, gql } from '@apollo/client';
import { toast } from 'react-toastify';
import { ProductInput } from '../../gql/graphql';
import Loader from '../../components/generic/Loader';

// Tab Components
import BasicInfoTab from '../../components/product/BasicInfoTab';
import PricingTaxTab from '../../components/product/PricingTaxTab';
import InventoryTab from '../../components/product/InventoryTab';
import AttributesTab from '../../components/product/AttributesTab';
import AnalyticsTab from '../../components/product/AnalyticsTab';
import AdvancedTab from '../../components/product/AdvancedTab';

const GET_PRODUCT_BY_ID = gql`
    query getProductById($id: Float!) {
        product(id: $id) {
            id
            sku
            name
            description
            quantity
            categoryId
            shopId
            locationId
            unit
            price
            costPrice
            taxPercentage
            isPerishable
            shelfLifeDays
            images
            isActive
            isAvailableOnline
            minOrderQuantity
            maxOrderQuantity
            preparationTime
            tags
            nutritionInfo
            commissionPercentage
        }
    }
`;

const ADD_UPDATE_PRODUCT = gql`
    mutation AddUpdateProduct($product: ProductInput!) {
        addUpdateProduct(product: $product) {
            id
            name
        }
    }
`;

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;
	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`product-tabpanel-${index}`}
			aria-labelledby={`product-tab-${index}`}
			{...other}
		>
			{value === index && <Box sx={{ py: 3 }}>{children}</Box>}
		</div>
	);
}

function ProductDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [currentTab, setCurrentTab] = useState(0);
	const [productData, setProductData] = useState<Partial<ProductInput>>({});
	const [isLoading, setIsLoading] = useState(false);

	const isEditMode = !!id && id !== 'new';

	const [getProductById] = useLazyQuery<Record<'product', ProductInput>>(
		GET_PRODUCT_BY_ID
	);

	const [addUpdateProduct, { loading: saving }] = useMutation(ADD_UPDATE_PRODUCT, {
		onCompleted: (data) => {
			toast.success(
				`Product: ${data.addUpdateProduct.name} ${isEditMode ? 'updated' : 'created'} successfully.`
			);
			if (!isEditMode) {
				navigate(`/dashboard/products/${data.addUpdateProduct.id}/edit`);
			}
		},
		onError: (error) => {
			toast.error(error.message);
		},
	});

	useEffect(() => {
		if (isEditMode) {
			loadProduct();
		}
	}, [id]);

	const loadProduct = async () => {
		setIsLoading(true);
		try {
			const { data } = await getProductById({
				variables: { id: parseFloat(id!) },
			});
			if (data?.product) {
				setProductData(data.product);
			}
		} catch (error: any) {
			toast.error(error.message);
		} finally {
			setIsLoading(false);
		}
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
		setCurrentTab(newValue);
	};

	const handleSave = async () => {
		try {
			await addUpdateProduct({
				variables: { product: productData },
			});
		} catch (error) {
			// Error handled in mutation onError
		}
	};

	const updateProductData = (updates: Partial<ProductInput>) => {
		setProductData((prev) => ({ ...prev, ...updates }));
	};

	return (
		<>
			<Loader isLoading={isLoading || saving} />
			<Box
				component="main"
				sx={{
					flexGrow: 1,
					py: 4,
				}}
			>
				<Container maxWidth="xl">
					{/* Header */}
					<Box sx={{ mb: 3 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
							<Button
								startIcon={<ArrowBack />}
								onClick={() => navigate('/dashboard/products')}
								sx={{ mr: 2 }}
							>
								Back
							</Button>
							<Box sx={{ flexGrow: 1 }}>
								<Typography variant="h4">
									{isEditMode ? 'Edit Product' : 'Create New Product'}
								</Typography>
								<Breadcrumbs sx={{ mt: 1 }}>
									<Link
										underline="hover"
										color="inherit"
										onClick={() => navigate('/dashboard/products')}
										sx={{ cursor: 'pointer' }}
									>
										Products
									</Link>
									<Typography color="text.primary">
										{isEditMode ? productData.name || 'Edit' : 'New Product'}
									</Typography>
								</Breadcrumbs>
							</Box>
							<Button
								variant="contained"
								startIcon={<Save />}
								onClick={handleSave}
								disabled={saving}
							>
								Save Product
							</Button>
						</Box>
					</Box>

					{/* Tabs */}
					<Card>
						<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
							<Tabs
								value={currentTab}
								onChange={handleTabChange}
								variant="scrollable"
								scrollButtons="auto"
							>
								<Tab label="Basic Information" />
								<Tab label="Pricing & Tax" />
								<Tab label="Inventory" disabled={!isEditMode} />
								<Tab label="Attributes" />
								<Tab label="Analytics" disabled={!isEditMode} />
								<Tab label="Advanced" />
							</Tabs>
						</Box>

						<CardContent>
							<TabPanel value={currentTab} index={0}>
								<BasicInfoTab
									productData={productData}
									updateProductData={updateProductData}
								/>
							</TabPanel>

							<TabPanel value={currentTab} index={1}>
								<PricingTaxTab
									productData={productData}
									updateProductData={updateProductData}
								/>
							</TabPanel>

							<TabPanel value={currentTab} index={2}>
								{isEditMode && (
									<InventoryTab
										productId={productData.id!}
										shopId={productData.shopId!}
										locationId={productData.locationId!}
									/>
								)}
							</TabPanel>

							<TabPanel value={currentTab} index={3}>
								<AttributesTab
									productData={productData}
									updateProductData={updateProductData}
								/>
							</TabPanel>

							<TabPanel value={currentTab} index={4}>
								{isEditMode && (
									<AnalyticsTab productId={productData.id!} />
								)}
							</TabPanel>

							<TabPanel value={currentTab} index={5}>
								<AdvancedTab
									productData={productData}
									updateProductData={updateProductData}
								/>
							</TabPanel>
						</CardContent>
					</Card>
				</Container>
			</Box>
		</>
	);
}

export default ProductDetailPage;
